import { useState, useMemo, useRef, useCallback } from "react";
import { Calculator, TrendingUp, ArrowRight } from "lucide-react";
import analytics from "@/utils/analytics";

/**
 * PartnerEarningsCalculator — presentational partner earnings estimator for
 * the /landing/partner-programme PPC page.
 *
 * Math is intentionally trivial and transparent (spec §10):
 *   monthly = activeMerchants × commissionPerMerchant
 *   annual  = monthly × 12
 *
 * This component does NOT submit anything to the CRM — it is an illustrative
 * widget only. It mirrors the visual language of the existing partner-income
 * calculators (card shell, aqua header, ResultCard tiles) but uses native
 * range inputs (keyboard-accessible, styled globally in App.css) instead of
 * the turnover-model engine.
 *
 * Analytics (fired via the shared dataLayer helper):
 *   partner_calculator_interaction          — first interaction only
 *   partner_calculator_merchant_count_change
 *   partner_calculator_commission_change
 *   partner_calculator_apply_click
 */

const MERCHANTS = { min: 1, max: 100, default: 10 };
const COMMISSION = { min: 25, max: 250, default: 50 };

function formatGBP(val) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

function ResultCard({ icon: Icon, label, value, variant = "default", testId }) {
  const bg =
    variant === "annual"
      ? "bg-gradient-to-br from-brand-aqua-light to-white border border-brand-aqua/30"
      : "bg-brand-aqua-light border border-brand-aqua/20";
  return (
    <div className={`${bg} rounded-xl p-4`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} className="text-brand-grey" />}
        <p className="text-xs font-bold uppercase tracking-wider text-brand-grey">{label}</p>
      </div>
      <p className="text-2xl sm:text-3xl font-heading font-bold text-brand-aqua-dark">{value}</p>
    </div>
  );
}

function SliderRow({ id, label, value, min, max, step, suffix, onChange }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label htmlFor={id} className="block text-sm font-semibold text-brand-black">
          {label}
        </label>
        <span className="text-sm font-heading font-bold text-brand-aqua-dark tabular-nums" aria-hidden="true">
          {`${suffix}${value}`}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={`${suffix}${value}`}
        className="w-full"
        data-testid={`${id}-slider`}
      />
      <div className="flex justify-between text-[11px] text-brand-grey mt-1">
        <span>
          {`${suffix}${min}`}
        </span>
        <span>
          {`${suffix}${max}`}
        </span>
      </div>
    </div>
  );
}

export default function PartnerEarningsCalculator({ applyHref = "#partner-application", className = "" }) {
  const [merchants, setMerchants] = useState(MERCHANTS.default);
  const [commission, setCommission] = useState(COMMISSION.default);
  const interactedRef = useRef(false);

  const markInteraction = useCallback(() => {
    if (interactedRef.current) return;
    // Keep the functional calculator state independent of analytics consent.
    // A denied interaction does not become a later replay, but a new input
    // after consent can still record the first eligible interaction.
    if (analytics.landingEvent("partner_calculator_interaction")) {
      interactedRef.current = true;
    }
  }, []);

  const handleMerchants = useCallback(
    (val) => {
      markInteraction();
      setMerchants(val);
    },
    [markInteraction],
  );

  const handleCommission = useCallback(
    (val) => {
      markInteraction();
      setCommission(val);
    },
    [markInteraction],
  );

  const { monthly, annual } = useMemo(() => {
    const m = merchants * commission;
    return { monthly: m, annual: m * 12 };
  }, [merchants, commission]);

  return (
    <div
      className={`bg-white border border-brand-light-grey rounded-2xl shadow-sm overflow-hidden ${className}`}
      data-testid="partner-earnings-calculator"
    >
      <div className="bg-brand-aqua-light px-6 sm:px-8 py-5 flex items-center gap-3 border-b border-brand-aqua/10">
        <div className="w-10 h-10 rounded-xl bg-brand-aqua flex items-center justify-center flex-shrink-0">
          <Calculator size={20} className="text-white" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-bold text-brand-black">Estimate your potential partner earnings</h3>
          <p className="text-xs text-brand-grey">Illustrative only — not a guarantee of earnings</p>
        </div>
      </div>

      <div className="p-6 sm:p-8 space-y-6">
        <SliderRow
          id="partner-calc-merchants"
          label="Active referred merchants"
          value={merchants}
          min={MERCHANTS.min}
          max={MERCHANTS.max}
          step={1}
          suffix=""
          onChange={handleMerchants}
        />
        <SliderRow
          id="partner-calc-commission"
          label="Average monthly commission per merchant"
          value={commission}
          min={COMMISSION.min}
          max={COMMISSION.max}
          step={5}
          suffix="£"
          onChange={handleCommission}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" aria-live="polite">
          <ResultCard
            icon={TrendingUp}
            label="Estimated monthly commission"
            value={formatGBP(monthly)}
            variant="primary"
            testId="partner-calc-monthly"
          />
          <ResultCard
            icon={TrendingUp}
            label="Estimated annual revenue"
            value={formatGBP(annual)}
            variant="annual"
            testId="partner-calc-annual"
          />
        </div>

        <a
          href={applyHref}
          onClick={() => analytics.landingEvent("partner_calculator_apply_click")}
          className="cta-primary text-white font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2 w-full"
          data-testid="partner-calc-apply"
          data-measurement-owned="landing"
        >
          Apply For The Partner Programme <ArrowRight size={16} />
        </a>

        <p className="text-xs text-brand-grey leading-relaxed">
          This calculator is for illustration only and does not guarantee earnings. Actual commission depends on
          merchant approval, processing volume, transaction mix, pricing, and ongoing merchant activity. Commission is
          payable only on approved merchants who go live and continue processing through CanDo Payments.
        </p>
      </div>
    </div>
  );
}
